import {
    BadgeAttachmentPoint,
    BadgeGeometry,
    BadgeGeometryInput,
    BadgeSide,
    getBadgeAttachmentPointFromGeometry,
    getBadgeGeometry,
} from './badgeGeometry'
import { ConnectionRoutingSettings, DEFAULT_CONNECTION_ROUTING_SETTINGS } from './connectionRoutingSettings'

export type BadgeRequestedSide = BadgeSide | 'auto'
export type EndpointRole = 'from' | 'to'

export interface BadgeEndpointDescriptor {
    connectionId: string
    badgeId: string
    role: EndpointRole
    requestedSide?: BadgeRequestedSide
}

export interface BadgeGeometryReference {
    id: string
    geometry: BadgeGeometryInput | BadgeGeometry
}

export interface ResolvedBadgeAttachment {
    endpoint: BadgeEndpointDescriptor
    identity: string
    side: BadgeSide
    slot: { index: number; count: number }
    point: BadgeAttachmentPoint
}

const EXPLICIT_SIDES = new Set<BadgeSide>(['top', 'right', 'bottom', 'left'])

function compareText(left: string, right: string): number {
    return left < right ? -1 : left > right ? 1 : 0
}

function resolvedGeometry(geometry: BadgeGeometryInput | BadgeGeometry): BadgeGeometry {
    if ('centerX' in geometry && 'centerY' in geometry && 'radius' in geometry) return geometry
    return getBadgeGeometry(geometry)
}

export function endpointIdentity(endpoint: BadgeEndpointDescriptor): string {
    return `${String(endpoint.connectionId)}:${endpoint.role}`
}

export function resolveAutoSide(from: BadgeGeometry, to: BadgeGeometry): BadgeSide {
    const dx = to.centerX - from.centerX
    const dy = to.centerY - from.centerY
    if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left'
    return dy >= 0 ? 'bottom' : 'top'
}

export function sortEndpointsForSlots(endpoints: BadgeEndpointDescriptor[]): BadgeEndpointDescriptor[] {
    return [...endpoints].sort((left, right) => compareText(endpointIdentity(left), endpointIdentity(right)))
}

function counterpartOf(
    endpoint: BadgeEndpointDescriptor,
    endpoints: BadgeEndpointDescriptor[],
): BadgeEndpointDescriptor | undefined {
    return endpoints.find(candidate =>
        candidate.connectionId === endpoint.connectionId
        && candidate.role !== endpoint.role,
    )
}

export function resolveEndpointSide(
    endpoint: BadgeEndpointDescriptor,
    endpoints: BadgeEndpointDescriptor[],
    geometries: ReadonlyMap<string, BadgeGeometry>,
): BadgeSide | null {
    if (EXPLICIT_SIDES.has(endpoint.requestedSide as BadgeSide)) return endpoint.requestedSide as BadgeSide
    const ownGeometry = geometries.get(endpoint.badgeId)
    const counterpart = counterpartOf(endpoint, endpoints)
    const otherGeometry = counterpart ? geometries.get(counterpart.badgeId) : undefined
    return ownGeometry && otherGeometry ? resolveAutoSide(ownGeometry, otherGeometry) : null
}

export function resolveBadgeAttachments(
    endpoints: BadgeEndpointDescriptor[],
    badges: BadgeGeometryReference[],
    settings: ConnectionRoutingSettings = DEFAULT_CONNECTION_ROUTING_SETTINGS,
): ResolvedBadgeAttachment[] {
    const geometries = new Map<string, BadgeGeometry>()
    for (const badge of badges ?? []) {
        if (!badge?.id || !badge.geometry) continue
        geometries.set(String(badge.id), resolvedGeometry(badge.geometry))
    }

    const groups = new Map<string, { badgeId: string; side: BadgeSide; endpoints: BadgeEndpointDescriptor[] }>()
    for (const endpoint of endpoints ?? []) {
        if (!endpoint?.connectionId || !endpoint.badgeId || !geometries.has(endpoint.badgeId)) continue
        if (endpoint.role !== 'from' && endpoint.role !== 'to') continue
        const side = resolveEndpointSide(endpoint, endpoints, geometries)
        if (!side) continue
        const key = `${endpoint.badgeId}:${side}`
        const group = groups.get(key) ?? { badgeId: endpoint.badgeId, side, endpoints: [] }
        group.endpoints.push(endpoint)
        groups.set(key, group)
    }

    const resolved: ResolvedBadgeAttachment[] = []
    for (const group of groups.values()) {
        const geometry = geometries.get(group.badgeId)
        if (!geometry) continue
        const ordered = [...group.endpoints].sort((left, right) => {
            const leftOther = counterpartOf(left, endpoints)
            const rightOther = counterpartOf(right, endpoints)
            const leftGeometry = leftOther ? geometries.get(leftOther.badgeId) : undefined
            const rightGeometry = rightOther ? geometries.get(rightOther.badgeId) : undefined
            const axis = group.side === 'left' || group.side === 'right' ? 'centerY' : 'centerX'
            const delta = (leftGeometry?.[axis] ?? geometry[axis]) - (rightGeometry?.[axis] ?? geometry[axis])
            return delta || compareText(endpointIdentity(left), endpointIdentity(right))
        })
        ordered.forEach((endpoint, index) => {
            resolved.push({
                endpoint,
                identity: endpointIdentity(endpoint),
                side: group.side,
                slot: { index, count: ordered.length },
                point: getBadgeAttachmentPointFromGeometry(
                    geometry, group.side, index, ordered.length, settings.connection_spacing,
                ),
            })
        })
    }
    return resolved.sort((left, right) => compareText(left.identity, right.identity))
}
